import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Building2, 
  Loader2,
  Search,
  Eye,
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

const AdminPropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });

  useEffect(() => {
    fetchProperties();
  }, [filters]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      
      const response = await api.get('/admin/properties', { params });
      setProperties(response.data.properties || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { className: 'bg-muted text-muted-foreground border-muted', label: 'Borrador', icon: null },
      submitted: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Enviado', icon: Clock },
      initial_review: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Revisión Inicial', icon: Clock },
      missing_info: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Info Faltante', icon: AlertCircle },
      legal_review: { className: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Revisión Legal', icon: Clock },
      technical_review: { className: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20', label: 'Revisión Técnica', icon: Clock },
      financial_structuring: { className: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20', label: 'Estructuración', icon: Clock },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Aprobado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: null },
      in_negotiation: { className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', label: 'En Negociación', icon: Clock },
      contracting: { className: 'bg-teal-500/10 text-teal-500 border-teal-500/20', label: 'Contratando', icon: Clock },
      closed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Cerrado', icon: CheckCircle2 },
    };
    const style = styles[status] || styles.draft;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={style.className}>
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {style.label}
      </Badge>
    );
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      residential: 'Residencial',
      commercial: 'Comercial',
      industrial: 'Industrial',
      land: 'Terreno',
      mixed_use: 'Uso Mixto',
      office: 'Oficinas',
      retail: 'Retail',
      hotel: 'Hotelería',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-8" data-testid="admin-properties-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Revisión de Inmuebles</h1>
          <p className="body-text">{total} inmuebles propuestos.</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título o ubicación..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-full md:w-56">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="submitted">Enviados</SelectItem>
                <SelectItem value="initial_review">Revisión Inicial</SelectItem>
                <SelectItem value="missing_info">Info Faltante</SelectItem>
                <SelectItem value="legal_review">Revisión Legal</SelectItem>
                <SelectItem value="technical_review">Revisión Técnica</SelectItem>
                <SelectItem value="financial_structuring">Estructuración</SelectItem>
                <SelectItem value="approved">Aprobados</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
                <SelectItem value="in_negotiation">En Negociación</SelectItem>
                <SelectItem value="contracting">Contratando</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Properties List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : properties.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-muted-foreground">No hay inmuebles pendientes de revisión</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {properties.map((property) => (
            <Card key={property.property_id} className="card-dashboard hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(property.status)}
                      <span className="text-xs text-muted-foreground">
                        {new Date(property.created_at).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                    <h3 className="font-medium mb-2">{property.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" strokeWidth={1.5} />
                        {property.city}, {property.state}
                      </span>
                      <span>{getPropertyTypeLabel(property.property_type)}</span>
                      {property.land_area_m2 && (
                        <span>{property.land_area_m2.toLocaleString()} m²</span>
                      )}
                    </div>
                    {property.user_name && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Propuesto por: {property.user_name} ({property.user_email})
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Link to={`/admin/properties/${property.property_id}`}>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                        Revisar
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPropertiesPage;
